import { axiosClientv1 } from "./apiClient";
import { ApiResponse } from "../Types";

export const GetRawMaterials = async (params: {
  page: number;
  limit: number;
  search?: string;
  supplier_id?: string | null;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/raw-materials`, { params });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load raw materials.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const CreateRawMaterial = async (
  formData: any,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/raw-materials/create`,
      formData,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to create raw material.",
      errors: error?.response?.data?.errors,
      error: {
        code: error?.response?.status || 400,
        type: "validation",
        errors: error?.response?.data?.errors,
      },
    };
  }
};

export const UpdateRawMaterial = async (
  formData: any,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/raw-materials/update`,
      formData,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to update raw material.",
      errors: error?.response?.data?.errors,
      error: {
        code: error?.response?.status || 400,
        type: "validation",
        errors: error?.response?.data?.errors,
      },
    };
  }
};

export const ToggleRawMaterialStatus = async (
  id: string,
  status: "Active" | "Inactive",
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/raw-materials/status`, {
      id,
      status,
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to change status.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const UpdateRawMaterialStockStatus = async (payload: {
  id?: string;
  stock_id?: string;
  status?: string;
  used_ft?: number;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/raw-materials/stock-status`,
      payload,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message ||
        "Failed to update stock batch status.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const DeleteRawMaterial = async (
  id: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/raw-materials/delete`, { id });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to delete raw material.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const GetRawPurchases = async (params: {
  page: number;
  limit: number;
  search?: string;
  supplier_id?: string | null;
  status?: string | null;
  payment_status?: string | null;
  store_id?: string | null;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/raw-purchases`, { params });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to load purchases.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const GetRawPurchaseById = async (
  id: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/raw-purchases/id`, {
      params: { id },
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load purchase details.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

const buildRawPurchasePayload = (data: any) => {
  if (data instanceof FormData) return data;

  const fileList = data.documents || [];
  const hasFiles =
    Array.isArray(fileList) &&
    fileList.some((f: any) => f?.originFileObj || f instanceof File);

  if (!hasFiles) {
    const existingDocs = Array.isArray(fileList)
      ? fileList
          .filter((f: any) => typeof f === "string" || f?.url)
          .map((f: any) => (typeof f === "string" ? f : f.url))
      : [];
    return {
      ...data,
      existing_documents: existingDocs,
    };
  }

  const fd = new FormData();
  Object.keys(data).forEach((key) => {
    if (key === "documents") {
      fileList.forEach((fileItem: any) => {
        if (fileItem?.originFileObj) {
          fd.append("documents[]", fileItem.originFileObj);
        } else if (fileItem instanceof File) {
          fd.append("documents[]", fileItem);
        } else if (typeof fileItem === "string") {
          fd.append("existing_documents[]", fileItem);
        } else if (fileItem?.url) {
          fd.append("existing_documents[]", fileItem.url);
        }
      });
    } else if (key === "items") {
      fd.append("items", JSON.stringify(data.items));
    } else if (data[key] !== undefined && data[key] !== null) {
      fd.append(
        key,
        typeof data[key] === "object" ? JSON.stringify(data[key]) : data[key],
      );
    }
  });
  return fd;
};

export const CreateRawPurchase = async (
  formData: any,
): Promise<ApiResponse<any>> => {
  try {
    const payload = buildRawPurchasePayload(formData);
    const response = await axiosClientv1.post(`/raw-purchases/create`, payload);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to record purchase.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const UpdateRawPurchase = async (
  formData: any,
): Promise<ApiResponse<any>> => {
  try {
    const payload = buildRawPurchasePayload(formData);
    const response = await axiosClientv1.post(`/raw-purchases/update`, payload);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to update purchase draft.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const DeleteRawPurchase = async (
  id: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/raw-purchases/delete`, { id });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to delete purchase draft.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const GetRecipe = async (
  productId?: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/recipes`, {
      params: productId ? { product_id: productId } : {},
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to load recipe.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const SaveRecipe = async (formData: any): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/recipes/save`, formData);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to save recipe.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const DeleteRecipe = async (id: string): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/recipes/delete`, { id });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to delete recipe.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const RecordRawPurchasePayment = async (data: {
  purchase_id: string;
  amount: number;
  payment_method_id: string;
  payment_date?: string;
  note?: string;
  bank_account_id?: string;
  transaction_no?: string;
  cheque_no?: string;
  bank_name?: string;
  cheque_date?: string;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/raw-purchases/payment/create`,
      data,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to record payment.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const GetRawMaterialSupplierPayments = async (params: {
  supplier_id: string;
  page: number;
  limit: number;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(
      `/raw-purchases/supplier-payments`,
      { params },
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load supplier payments.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};
